import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import * as lodash from 'lodash'
import { of, throwError, Observable } from 'rxjs'
import { catchError, tap, finalize, shareReplay } from 'rxjs/operators'

type HTTPGetOptions = Parameters<HttpClient['get']>[1]
type HTTPPostOptions = Parameters<HttpClient['post']>[2]
type HTTPPutOptions = Parameters<HttpClient['put']>[2]
type CacheOptions = { refresh?: boolean; ttl?: number; cacheKey: string }

interface CacheEntry<T> {
  data: T
  expires: number
}

// Must be a single app-wide instance: the cache and the in-flight `ongoingRequests` map are
// what coalesce N subscribers into one HTTP call. Registering it in Tv3PlatformModule.providers
// gave every lazy-loaded feature module its own copy, so two features asking for the same URL
// in the same tick still made two requests.
@Injectable({ providedIn: 'root' })
export class HttpGetRequestCacheService {
  private cache = new Map<string, CacheEntry<any>>()
  private ongoingRequests = new Map<string, Observable<any>>()

  constructor(private http: HttpClient) {}

  cacheGetRequest<T = any>(url: string, options?: boolean | (HTTPGetOptions & Partial<CacheOptions>)): Observable<T> {
    const normalizedOptions = (lodash.isBoolean(options) ? { refresh: options } : options) || {}

    const request$ = this.http.get<T>(url, normalizedOptions)

    const cacheKey = this.generateCacheKey('GET', url, normalizedOptions.params)
    return this.cacheReq<T>(request$, { ...normalizedOptions, cacheKey: normalizedOptions.cacheKey ?? cacheKey })
  }

  cachePostRequest<T = any>(
    url: string,
    body: any,
    options?: boolean | (HTTPPostOptions & Partial<CacheOptions>)
  ): Observable<T> {
    const normalizedOptions = (lodash.isBoolean(options) ? { refresh: options } : options) || {}

    const request$ = this.http.post<T>(url, body, normalizedOptions)

    const cacheKey = this.generateCacheKey('POST', url, normalizedOptions.params, body)
    return this.cacheReq<T>(request$, { ...normalizedOptions, cacheKey: normalizedOptions.cacheKey ?? cacheKey })
  }

  cachePutRequest<T = any>(
    url: string,
    body: any,
    options?: boolean | (HTTPPutOptions & Partial<CacheOptions>)
  ): Observable<T> {
    const normalizedOptions = (lodash.isBoolean(options) ? { refresh: options } : options) || {}

    const request$ = this.http.put<T>(url, body, normalizedOptions)

    const cacheKey = this.generateCacheKey('PUT', url, normalizedOptions.params, body)
    return this.cacheReq<T>(request$, { ...normalizedOptions, cacheKey: normalizedOptions.cacheKey ?? cacheKey })
  }

  private generateCacheKey(method: string, url: string, params: any, payload?: any) {
    return `${method}-${url}-${JSON.stringify(params)}-${JSON.stringify(payload)}`
  }

  private cacheReq<T = any>(request$: Observable<T>, options: CacheOptions): Observable<T> {
    const { refresh = false, ttl, cacheKey } = options
    const key = cacheKey

    if (refresh) {
      this.cache.delete(key)
      this.ongoingRequests.delete(key)
    }

    // Check if we have cached data and it's still valid
    if (this.cache.has(key)) {
      const cached = this.cache.get(key)!
      const now = Date.now()

      // If TTL is 0, don't use cache (immediate expiration)
      if (ttl === 0) {
        this.cache.delete(key)
      } else if (ttl === undefined || cached.expires > now) {
        // Cache is still valid (either indefinite or not expired)
        return of(cached.data as T)
      } else {
        // Cache has expired, remove it
        this.cache.delete(key)
      }
    }

    // Check if there's already an ongoing request for this URL
    if (this.ongoingRequests.has(key)) {
      return this.ongoingRequests.get(key) as Observable<T>
    }

    const shared$ = request$.pipe(
      tap((data) => {
        // Cache if TTL is undefined (indefinite) or > 0 (with expiration)
        if (ttl === undefined || ttl > 0) {
          const expires = ttl === undefined ? Number.MAX_SAFE_INTEGER : Date.now() + ttl
          this.cache.set(key, { data, expires })
        }
      }),
      catchError((error) => {
        // Remove cache entry on error to prevent serving stale data
        this.cache.delete(key)
        return throwError(error)
      }),
      finalize(() => {
        // Remove from ongoing requests when the request completes (success or error)
        this.ongoingRequests.delete(key)
      }),
      // http observables are cold — without sharing, a concurrent caller that picks this
      // entry from ongoingRequests would trigger a second identical request on subscribe
      shareReplay({ bufferSize: 1, refCount: false })
    )

    // Store the ongoing request
    this.ongoingRequests.set(key, shared$)

    return shared$
  }
}
